import {
  RUG_PROOF_STAKING_OWNER_BADGE_ADDRESS,
  NODE_STAKING_OWNER_BADGE_ADDRESS,
  XUSDC_RESOURCE_ADDRESS,
} from "Constants/address";
import { radixDashboardBaseUrl } from "Constants/misc";
import { MorpherPriceData, ResourceDetails } from "Types/api";
import { parseUnits as parseUnitsEthers } from "ethers";
import BigNumber from "bignumber.js";
import numbro from "numbro";
import { RewardTokenDistribution } from "Types/token";
import { FungibleResourcesCollectionItem } from "@radixdlt/babylon-gateway-api-sdk";
import { dispatch, store } from "Store";
import { setRewardsModalData } from "Store/Reducers/session";
import { StakingTokens } from "Types/reducers";
import { twMerge } from "tailwind-merge";
import { type ClassValue, clsx } from "clsx";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const BN = BigNumber.clone({
  DECIMAL_PLACES: 18,
  ROUNDING_MODE: BigNumber.ROUND_HALF_UP,
  EXPONENTIAL_AT: [-20, 20],
});

export const formatDollarAmount = (num: number | undefined | null, digits = 2, round = true) => {
  if (num === 0) return "$0.00";
  if (!num) return "-";
  if (num < 0.001 && digits <= 3) {
    return "<$0.001";
  }
  if (num < 0.01 && digits <= 2) {
    return "<$0.01";
  }

  return numbro(num).formatCurrency({
    average: round,
    mantissa: num > 1000 ? 2 : digits,
    abbreviations: {
      million: "M",
      billion: "B",
    },
  });
};

export const formatTokenAmount = (num: number | undefined, digits = 2) => {
  if (num === 0) return "0";
  if (!num) return "-";
  if (num < 0.001 && digits <= 3) {
    return "<0.001";
  }
  if (num < 0.01 && digits <= 2) {
    return "<0.01";
  }

  let formattedAmount = numbro(num)
    .formatCurrency({
      average: true,
      mantissa: num >= 1000 ? 2 : digits,
      abbreviations: {
        million: "M",
        billion: "B",
      },
    })
    .replace("$", "");

  formattedAmount = formattedAmount.replace(".00", "");
  return formattedAmount;
};

export const exactAmountInDecimals = (amount: number, decimals: number) => {
  return Number.isInteger(amount) ? amount.toString() : amount.toFixed(decimals).replace(/0+$/, "");
};

export function parseUnits(_num: number) {
  return parseUnitsEthers(_num.toString(), 18).toString();
}

export function formatUnits(_num: number, decimals: number) {
  const divisor = 10 ** decimals;
  return _num / divisor;
}

export const generateExplorerTxLink = (txId?: string) => {
  return `${radixDashboardBaseUrl}/transaction/${txId}`;
};

export const extractBalances = (
  resources: ResourceDetails[],
  tokens: {
    symbol: string;
    address: string;
  }[],
  searchForOwner: boolean = false
) => {
  const balances: { [symbol: string]: string } = {};
  let isOwner = false;

  // Initialize all token balances to "0"
  for (const token of tokens) {
    balances[token.symbol] = "0";
  }

  for (const resource of resources) {
    const token = tokens.find((t) => t.address === resource.resource_address);
    if (token) {
      balances[token.symbol] = resource.amount;
    } else if (
      searchForOwner &&
      (resource.resource_address === RUG_PROOF_STAKING_OWNER_BADGE_ADDRESS ||
        resource.resource_address === NODE_STAKING_OWNER_BADGE_ADDRESS) &&
      +resource.amount > 0
    ) {
      isOwner = true;
    }

    // Break the loop if all balances are found and check isOwner is true only if searchForOwner is true
    if (
      tokens.every((token) => balances[token.symbol] !== "0") &&
      (searchForOwner ? isOwner === true : true)
    ) {
      break;
    }
  }

  return {
    balances,
    isOwner,
  };
};

export const extractBalancesNew = (
  resources: FungibleResourcesCollectionItem[],
  tokens: {
    symbol: string;
    address: string;
  }[],
  searchForOwner: boolean = false
) => {
  const balances: { [symbol: string]: string } = {};
  let isOwner = false;

  // Initialize all token balances to "0"
  for (const token of tokens) {
    balances[token.symbol] = "0";
  }

  for (const resource of resources) {
    if (resource.aggregation_level === "Global") {
      const token = tokens.find((t) => t.address === resource.resource_address);
      if (token) {
        balances[token.symbol] = resource.amount;
      } else if (
        searchForOwner &&
        (resource.resource_address === RUG_PROOF_STAKING_OWNER_BADGE_ADDRESS ||
          resource.resource_address === NODE_STAKING_OWNER_BADGE_ADDRESS) &&
        +resource.amount > 0
      ) {
        isOwner = true;
      }

      // Break the loop if all balances are found and check isOwner is true only if searchForOwner is true
      if (
        tokens.every((token) => balances[token.symbol] !== "0") &&
        (searchForOwner ? isOwner === true : true)
      ) {
        break;
      }
    }
  }

  return {
    balances,
    isOwner,
  };
};

export const conciseAddress = (address: string, startSlice = 3, endSlice = 3): string => {
  return `${address?.slice(0, startSlice)}...${address?.slice(
    address?.length - endSlice,
    address?.length
  )}`;
};

export const toLocaleFormat = (value: string) => {
  if (value === "") {
    return value;
  }
  // Remove all non-digit and non-decimal characters
  value = value.replace(/[^0-9.]/g, "");
  const containsDot = value.includes(".");

  // Split into integer and decimal parts
  const parts = value.split(".");
  const integerPart = parts[0];
  const decimalPart = parts.length > 1 ? parts[1] : "";

  // Format the integer part using toLocaleString()
  const formattedIntegerPart = parseInt(integerPart).toLocaleString();

  // Combine integer part and decimal part
  const answer =
    decimalPart || containsDot ? formattedIntegerPart + "." + decimalPart : formattedIntegerPart;
  return answer;
};

export const formatRewardTokenDistribution = (
  rewardTokenDistributions: RewardTokenDistribution[]
) => rewardTokenDistributions.map(({ id, amount }) => `${id}u64 => Decimal("${amount}")`).join(",");

export const calculateEstimatedUnlockDate = (epochUnlocked: number, currentEpoch: number) => {
  const minutesPerEpoch = 5;
  const currentDate = new Date();
  const unlockDate = new Date(
    currentDate.getTime() + (epochUnlocked - currentEpoch) * minutesPerEpoch * 60000
  );
  return unlockDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const calculateAvgShareOfSnapshots = (amount: string) => {
  const selectedSnapshots = store.getState().session.selectedSnapshots;
  const totalAmounts: { [id: number]: string } = {};
  const snapshotCount = selectedSnapshots.length;

  selectedSnapshots.forEach((snapshot) => {
    snapshot.data.forEach((distribution) => {
      const { id, amount } = distribution;
      if (!totalAmounts[id]) {
        totalAmounts[id] = "0";
      }
      totalAmounts[id] = new BN(totalAmounts[id]).plus(amount).toString();
    });
  });

  const avgShares: RewardTokenDistribution[] = [];
  for (const id in totalAmounts) {
    avgShares.push({
      id: parseInt(id),
      amount: new BN(totalAmounts[id]).dividedBy(snapshotCount).multipliedBy(amount).toString(),
    });
  }

  dispatch(
    setRewardsModalData({
      amount,
      RewardTokenDistributions: avgShares,
      tokenSymbol: StakingTokens.XUSDC,
      tokenAddress: XUSDC_RESOURCE_ADDRESS,
      snapshot: 0,
      timestamp: 0,
    })
  );
  (document.getElementById("DistributionModal") as HTMLDialogElement).showModal();
};

export function calculateYearlyAPY(apr7d: number): number {
  const periodsPerYear = 365 / 7;
  const aprDecimal = apr7d / 100;

  const apyDecimal = Math.pow(1 + aprDecimal, periodsPerYear) - 1;
  const apyPercentage = apyDecimal * 100;

  return apyPercentage;
}

export function priceMsgToMorpherString(msg: MorpherPriceData) {
  return msg.data
    .map((el) => el.marketId + "-" + el.price + "-" + el.nonce + "-" + el.createdAt)
    .join(",");
}
